/**
 * cases.ts — the wire shapes for projects, saved cases, recalculation and import (P2 wave 3).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THESE LIVE HERE AND NOT IN THE ROUTES THAT SERVE THEM
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * P0's metric M9: *if a shape is defined twice in this repository, that is the bug*. A route handler
 * is the single most likely place for the second copy to appear, because it is where a shape is
 * most convenient to type inline — and the browser (US-212..215) has to render every one of these.
 * Defining them here is what stops the API and the UI from quietly disagreeing about what a saved
 * case is.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE SAVE REQUEST CARRIES INPUTS. IT CANNOT CARRY RESULTS.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * {@link saveCaseRequestSchema} is a **strict** object, so a client that sends `results` gets a 422
 * rather than having them silently dropped. Silently dropping would be defensible engineering and
 * terrible communication: the client would believe its snapshot had been stored, and the whole
 * value of the `cases` table is that somebody can vouch for what is in it (D7, ARCHITECTURE §6).
 * A loud refusal says "the server computes this" at the only moment anyone is listening.
 */

import { z } from 'zod';

import { rowIdSchema, sha256HexSchema } from './db';
import { ROOT_FIELD_PATH, fieldErrorSchema, type InvalidInputError } from './errors';
import {
  CalcResponseSchema,
  CheckStatusSchema,
  engineNumber,
  engineNumberOrNull,
} from './response';
import { savedModuleSchema } from './saved-file';

/* ================================================================================================
 * Pagination
 * ============================================================================================== */

/**
 * A pilot organisation with 4 000 cases must not receive them in one response — not because the
 * response would be slow, but because the Worker would build the whole array in memory inside a
 * CPU budget the P0 spike already showed is this platform's binding constraint.
 */
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;

/**
 * `?limit=&offset=`, coerced from strings because that is what a query string is.
 *
 * An out-of-range `limit` is clamped by the data layer rather than rejected: asking for 5 000 rows
 * is an optimistic client, not a malformed request, and answering with 200 rows is more useful than
 * answering with a 422.
 */
export const paginationQuerySchema = z.object({
  limit: z.coerce.number().int().positive().default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/* ================================================================================================
 * Projects
 * ============================================================================================== */

export const createProjectRequestSchema = z.strictObject({
  name: z
    .string()
    .min(1)
    .max(200)
    .describe('Project name, e.g. "Halle logistique Saint-Denis". Dimensionless.'),
  address: z
    .string()
    .max(500)
    .nullish()
    .describe('Free text, printed on the note de calcul. Dimensionless.'),
});
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;

/**
 * A project as the API returns it.
 *
 * `camelCase` here, `snake_case` in `./db`. That is not a style lapse: the row schema describes what
 * SQLite actually holds and the response schema describes the wire, and pretending they are one
 * shape would mean either a database with camelCase columns or an API that leaks its storage
 * layout. The mapping is one function, in `apps/api/src/routes/projects.ts`.
 */
export const projectResponseSchema = z.object({
  id: rowIdSchema,
  name: z.string().min(1),
  address: z.string().nullable(),
  createdAt: z.string().min(1).describe('SQLite UTC timestamp, `YYYY-MM-DD HH:MM:SS`.'),
  updatedAt: z.string().min(1),
});
export type ProjectResponse = z.infer<typeof projectResponseSchema>;

export const projectListResponseSchema = z.object({
  projects: z.array(projectResponseSchema),
  limit: z.int(),
  offset: z.int(),
});
export type ProjectListResponse = z.infer<typeof projectListResponseSchema>;

/* ================================================================================================
 * Cases
 * ============================================================================================== */

/** `{ module, title?, inputs }` — and nothing else. See the file header on why this is strict. */
export const saveCaseRequestSchema = z.strictObject({
  module: savedModuleSchema,
  title: z
    .string()
    .max(200)
    .nullish()
    .describe('User-supplied label, e.g. "Poutre de plancher N3". Dimensionless.'),
  inputs: z
    .record(z.string(), z.unknown())
    .describe(
      'The calculation request, validated against the module schema from `./request` by the ' +
        'route. Typed loosely HERE so that this envelope does not become a fourth place the ' +
        'per-module input shapes are written down.',
    ),
});
export type SaveCaseRequest = z.infer<typeof saveCaseRequestSchema>;

/**
 * A case without its payloads — what a list returns.
 *
 * The versions and the integrity hash are in the summary rather than only in the full record, so a
 * list can show "computed by engine 1.0.0, catalog 2026.08" without fetching every snapshot. That
 * is the column an engineer scans when asking which of forty cases predate an engine release.
 */
export const caseSummarySchema = z.object({
  id: rowIdSchema,
  projectId: rowIdSchema,
  module: savedModuleSchema,
  title: z.string().nullable(),
  schemaVersion: z.int().positive(),
  engineVersion: z.string().min(1),
  catalogVersion: z.string().min(1),
  integrityHash: sha256HexSchema,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
  deletedAt: z.string().nullable(),
});
export type CaseSummary = z.infer<typeof caseSummarySchema>;

/**
 * The full stored record: the summary plus the two snapshots.
 *
 * `results` is the **stored** snapshot, never a fresh calculation (D10). Opening a case from 2027 in
 * 2031 shows what the tool said in 2027, because that is what the note de calcul was issued from.
 * Asking what today's engine says is a different question, and it has its own route.
 */
export const caseResponseSchema = caseSummarySchema.extend({
  inputs: z.record(z.string(), z.unknown()),
  results: CalcResponseSchema,
});
export type CaseResponse = z.infer<typeof caseResponseSchema>;

export const caseListResponseSchema = z.object({
  cases: z.array(caseSummarySchema),
  limit: z.int(),
  offset: z.int(),
});
export type CaseListResponse = z.infer<typeof caseListResponseSchema>;

/**
 * The answer to `DELETE /v1/cases/:id`.
 *
 * It reports `deletedAt` rather than returning `204 No Content`, because the operation is a soft
 * delete (D11, FR-15) and a caller deserves to be told that in the response rather than discovering
 * it later. There is no hard delete in this product.
 */
export const deleteCaseResponseSchema = z.object({
  id: rowIdSchema,
  deleted: z.literal(true),
  deletedAt: z.string().min(1),
});
export type DeleteCaseResponse = z.infer<typeof deleteCaseResponseSchema>;

/* ================================================================================================
 * Recalculation (US-208, D10)
 * ============================================================================================== */

/**
 * What happened to one check between the stored snapshot and today's engine.
 *
 * Four outcomes, three of which appear here — unchanged checks are counted, not listed, because a
 * diff that repeats the eleven checks that did not move buries the one that did.
 */
export const CHECK_DIFF_KINDS = ['changed', 'appeared', 'disappeared'] as const;
export const checkDiffKindSchema = z
  .enum(CHECK_DIFF_KINDS)
  .describe(
    '`changed`: the check exists in both and its ratio or status moved. `appeared`: the current ' +
      'engine emits a check the snapshot has not. `disappeared`: the snapshot has a check the ' +
      'current engine no longer emits.',
  );
export type CheckDiffKind = z.infer<typeof checkDiffKindSchema>;

export const checkDiffSchema = z.object({
  id: z
    .string()
    .min(1)
    .describe(
      'The stable check id from the register. THE ALIGNMENT KEY — never position, never label: ' +
        'bois emits duplicate labels, and an engine that inserted a check would shift every ' +
        'positional comparison after it by one.',
    ),
  label: z.string().describe('The French label, for display. Taken from whichever side exists.'),
  kind: checkDiffKindSchema,
  beforeRatio: engineNumberOrNull.describe('Utilisation ratio in the stored snapshot, or null.'),
  afterRatio: engineNumberOrNull.describe('Utilisation ratio now, or null.'),
  beforeStatus: CheckStatusSchema.nullable(),
  afterStatus: CheckStatusSchema.nullable(),
});
export type CheckDiff = z.infer<typeof checkDiffSchema>;

export const versionStampSchema = z.object({
  engineVersion: z.string().min(1),
  catalogVersion: z.string().min(1),
});
export type VersionStamp = z.infer<typeof versionStampSchema>;

/**
 * `GET /v1/version` — what is running.
 *
 * The stamp plus the catalog's content hash. Written down here rather than typed inline in the
 * route because the browser now reads it: US-215's banner compares a stored case's versions against
 * these, and a banner that appeared or vanished because the two sides disagreed about the shape of
 * this body would be worse than no banner.
 *
 * `catalogVersion` is what a human bumps; `catalogHash` is what actually changed, and it is the
 * value the golden baseline is pinned to.
 */
export const versionResponseSchema = versionStampSchema.extend({
  catalogHash: sha256HexSchema.describe(
    'Content hash of the catalog. The baseline is pinned to it.',
  ),
});

export type VersionResponse = z.infer<typeof versionResponseSchema>;

/**
 * The whole comparison.
 *
 * `identical: true` is a **first-class, useful answer**, not an empty result. "Nothing has changed
 * since you issued that note" is precisely what an engineer opening a two-year-old case wants to
 * hear, and an API that expressed it only as an empty array would make them squint at it.
 */
export const recalcDiffSchema = z.object({
  identical: z.boolean(),
  from: versionStampSchema.describe('Versions that produced the stored snapshot.'),
  to: versionStampSchema.describe('Versions running now.'),
  checks: z
    .array(checkDiffSchema)
    .describe('Only the checks that differ. Unchanged ones are counted, not listed.'),
  unchangedCount: z.int().min(0),
  maxBefore: engineNumber.describe('Governing ULS utilisation in the stored snapshot.'),
  maxAfter: engineNumber.describe('Governing ULS utilisation now.'),
});
export type RecalcDiff = z.infer<typeof recalcDiffSchema>;

export const recalcPreviewResponseSchema = z.object({
  caseId: rowIdSchema,
  diff: recalcDiffSchema,
  results: CalcResponseSchema.describe(
    'What the current engine produces. This route stores nothing.',
  ),
});
export type RecalcPreviewResponse = z.infer<typeof recalcPreviewResponseSchema>;

export const recalcConfirmResponseSchema = z.object({
  caseId: rowIdSchema,
  revisionId: rowIdSchema.describe('The archived prior snapshot. Immutable, and never deleted.'),
  diff: recalcDiffSchema,
  case: caseResponseSchema.describe('The case as it now stands, re-stamped.'),
});
export type RecalcConfirmResponse = z.infer<typeof recalcConfirmResponseSchema>;

/* ================================================================================================
 * Legacy import (US-209)
 * ============================================================================================== */

export const importedCaseSchema = z.object({
  caseId: rowIdSchema,
  module: savedModuleSchema,
  title: z.string().nullable(),
});
export type ImportedCase = z.infer<typeof importedCaseSchema>;

/**
 * One case in the file that could not be imported — reported **beside** the ones that were.
 *
 * A file carries up to three cases (`invalid-cases/README.md`), and one bad module must not lose
 * the other two. An importer that failed the whole file on the first bad case would send a pilot
 * user back to retyping, which is the outcome US-209 exists to prevent.
 */
export const skippedCaseSchema = z.object({
  module: savedModuleSchema,
  reason: z.literal('invalid'),
  fields: z.array(fieldErrorSchema).min(1),
});
export type SkippedCase = z.infer<typeof skippedCaseSchema>;

export const importResponseSchema = z.object({
  projectId: rowIdSchema,
  fileVersion: z
    .union([z.literal(0), z.literal(1)])
    .describe('Which saved-file schema the file used. A v0 file imports with no warning.'),
  projectName: z.string().describe('The `projet` field of the file, for display. Not applied.'),
  imported: z.array(importedCaseSchema),
  skipped: z.array(skippedCaseSchema),
});
export type ImportResponse = z.infer<typeof importResponseSchema>;

/**
 * The field code a refused **newer** file carries.
 *
 * "This file is from a newer version of the tool" and "this file is corrupt" need different actions
 * from the user — upgrade, versus find a good copy — so they must be distinguishable without
 * reading the French prose. `fieldErrorSchema.code` is a free string precisely so that a new code
 * like this one does not require a client to be redeployed before it can parse the body.
 */
export const UNSUPPORTED_VERSION_CODE = 'unsupported_version';

/* ================================================================================================
 * Helpers
 * ============================================================================================== */

/**
 * Re-root a `422` under a parent field.
 *
 * `POST /cases` validates `body.inputs` with the module's own request schema, which naturally
 * reports `MyEd` rather than `inputs.MyEd`. Prefixing here — once, in the contract — keeps the
 * route from hand-rolling a second mapping over Zod issues, which is the exact duplication
 * `zodErrorTo422Body` exists to prevent.
 *
 * An issue at the schema root becomes the prefix itself: `inputs`, not `inputs.(root)`.
 */
export function prefixFieldPaths(body: InvalidInputError, prefix: string): InvalidInputError {
  return {
    ...body,
    fields: body.fields.map((field) => ({
      ...field,
      path: field.path === ROOT_FIELD_PATH ? prefix : `${prefix}.${field.path}`,
    })),
  };
}
